import generateTimelineViewIndexes from '../../helpers/generateTimelineViewIndexes';
import storyZoomMapper from '../../helpers/storyZoomMapper';

export const ActiveRoutes = {
    ExploreView: 'exploreView',
    LandingView: 'landingView',
    TimelineView: 'timelineView',
    ChaptersView: 'chaptersView',
    BookView: 'bookView',
    MenuView: 'menuView',
    StoryView: 'storyView'
};

/**
 * This module will be handling global app data
 *
 * @returns {undefined}
 */
const appData = () => {
    const firstRenderStory = document.body.dataset.activeStory;
    let activeRoute = '';
    let viewData = [];
    let timelineViewData = [];
    let filterData = {
        count: 0
    };
    let selectedFilterData = [];
    let activeStoryData = [];
    let isInitialized = false;
    let config = null;
    let contentWorker = null;
    let indexes = null;
    let zoomViewData = [];
    let storyViewReferrer = '';
    let isAppMounted = false;
    let isLoading = true; // this variable will decide if app is loading/animating

    // Load global configuration from the body tag, set server side
    try {
        config = JSON.parse(document.body.dataset.config250);
    } catch (error) {
        console.error('Error parsing global config JSON', error.message);
    }

    // If no valid config object, the worker and API cannot be used
    if (config !== null) {
        // Webpack does not work well with dynamic resource path. Therefore, setting the path to import from on runtime
        // eslint-disable-next-line no-undef,camelcase
        __webpack_public_path__ = config.webpackResourceBasePath;

        // Create a web worker
        contentWorker = new Worker(new URL('../../workers/core.js', import.meta.url), {
            type: 'module'
        });

        // Get local content
        const apiGetContentUrl = config.urls.getContent;
        let saveItemsToLocalStorage = true;
        let localStorageItems = null;

        if (localStorage.getItem('bs_250') !== null) {
            let localStorageData = null;

            try {
                localStorageData = JSON.parse(localStorage.getItem('bs_250'));
            } catch {
                // No valid local storage? Grab a fresh one from the server
            }

            // Validate the local content matches the current locale
            if (
                localStorageData !== null &&
                config.locale === localStorageData.locale &&
                localStorageData.expiresAt > Date.now()
            ) {
                localStorageItems = localStorageData.items;
                saveItemsToLocalStorage = false;
            }
        }

        // Initialize web worker
        contentWorker.postMessage({action: 'init', config, localStorageItems, apiGetContentUrl, firstRenderStory});

        /**
         * Receive the filtered array from the worker
         *
         * @param {Object} event Message event data
         */
        contentWorker.onmessage = event => {
            if (event.data.action === 'init') {
                if (event.data.result && event.data.result.success === true) {
                    // All items default sorted by year
                    timelineViewData = event.data.result.items;
                    isInitialized = true;

                    // Save items in Local Storage to be reused
                    if (saveItemsToLocalStorage) {
                        localStorage.setItem(
                            'bs_250',
                            JSON.stringify({
                                locale: config.locale,
                                expiresAt: Date.now() + config.expiresIn,
                                items: event.data.result.items
                            })
                        );
                    }

                    // Get all refinements
                    contentWorker.postMessage({action: 'refinements'});

                    // Get active story
                    contentWorker.postMessage({action: 'getActiveStory'});
                }
            } else if (event.data.action === 'random') {
                // Save data which can be used via appData.viewData
                viewData.push(...event.data.result);
            } else if (event.data.action === 'refinements') {
                // Save data which can be used via appData.filterData
                filterData = event.data.result;
            } else if (event.data.action === 'getActiveStory') {
                // Save data which can be used via appData.activeStoryData
                activeStoryData = event.data.result;
            } else if (event.data.action === 'filter') {
                // Update explorer view dataset
                if (event.data.isPreview !== true) {
                    viewData = event.data.result;
                }
            }

            const contentWorkerEvent = new CustomEvent('content.worker.done', {
                bubbles: true,
                cancelable: false,
                detail: event.data
            });
            window.dispatchEvent(contentWorkerEvent);
        };
    }

    /**
     * Function to fetch related data
     *
     * @param {Object} contentItem - data related to specific story
     * @returns {undefined}
     */
    const triggerRelatedData = contentItem => {
        contentWorker.postMessage({
            action: 'related',
            contentItem: contentItem
        });
    };

    /**
     * Get a random content set
     *
     * @param {number} max - The maximum number of elements to include in the subset.
     * @param {Object} context details passed to the worker
     */
    const getContent = (max, context) => {
        if (isInitialized) {
            contentWorker.postMessage({action: 'random', max: max, context: context});
        }
    };

    /**
     * Get the sorted content by year
     *
     * @returns {[any]} timelineViewData - data for TimelineView
     */
    const getTimelineViewData = () => {
        if (isInitialized) {
            if (!indexes) {
                indexes = generateTimelineViewIndexes(timelineViewData);
            }
            return timelineViewData;
        }
        return [];
    };

    /**
     * Get/Generate indexes for the timelineView
     *
     * @returns {Object} indexes -
     */
    const getTimelineViewIndexes = () => {
        if (!indexes) {
            indexes = generateTimelineViewIndexes(timelineViewData);
        }
        return indexes;
    };

    /**
     * Apply selected filters to the explorer dataset
     *
     * @param {boolean} isPreview - Set to true to not apply the filters to the explore data set
     * @param {Object} selectedFilters - All selected values of each filter option
     */
    const applyFilter = (isPreview, selectedFilters) => {
        if (isInitialized) {
            if (!isPreview) {
                selectedFilterData = selectedFilters;
            }

            contentWorker.postMessage({
                action: 'filter',
                isPreview,
                selectedFilters
            });
        }
    };

    /**
     * Sets active route/page
     *
     * @param {string} value - Active route/page
     * @returns {undefined}
     */
    const setActiveRoute = value => {
        activeRoute = value;
    };

    /**
     * Gets active route/page
     *
     * @returns {undefined}
     */
    const getActiveRoute = () => {
        return activeRoute;
    };

    /**
     * Sets the active story data
     *
     * @param {string} itemId Id of the item to set as the active one
     * @returns {Object} Active Story Data
     */
    const setActiveStory = itemId => {
        // Skip if the active story is the same
        if (activeStoryData && activeStoryData.id === itemId) {
            return activeStoryData;
        }

        activeStoryData = timelineViewData.find(item => item.id === itemId);

        // Notify the active story has changed
        const activeStoryEvent = new CustomEvent('250.active.story', {
            bubbles: true,
            cancelable: false,
            detail: {
                id: itemId
            }
        });

        window.dispatchEvent(activeStoryEvent);

        return activeStoryData;
    };

    /**
     * Returns the active story data
     *
     * @returns {Object} Active Story Data
     */
    const getActiveStory = () => {
        return activeStoryData;
    };

    /**
     * Returns all possible refinements
     *
     * @returns {Array} List of all possible refinements
     */
    const getFilterData = () => {
        return filterData;
    };

    /**
     * Gets the latest selected filter data
     *
     * @returns {Array} List of all selected refinements
     */
    const getSelectedFilterData = () => {
        return selectedFilterData;
    };

    /**
     * Sets zoom story data
     *
     * @param {Array} storyData - story data
     * @returns {undefined}
     */
    const setZoomStoryData = storyData => {
        zoomViewData = storyZoomMapper(storyData);
    };

    /**
     * Gets zoom story data
     *
     * @returns {Array} - zoom story data
     */
    const getZoomStoryData = () => {
        return zoomViewData;
    };

    /**
     * Sets story page referrer
     *
     * @param {string} referrer - story page referrer
     * @returns {undefined}
     */
    const setStoryViewReferrer = referrer => {
        storyViewReferrer = referrer;
    };

    /**
     * Gets story page referrer
     *
     * @returns {string} - story page referrer
     */
    const getStoryViewReferrer = () => {
        return storyViewReferrer;
    };

    /**
     * Returns if the content worker is initialized or not
     *
     * @returns {boolean} True if the content worker is initialized
     */
    const isReady = () => {
        return isInitialized;
    };

    /**
     * Return true if app is loaded
     *
     * @returns {boolean} - true/false
     */
    const getIsAppMounted = () => {
        return isAppMounted;
    };

    /**
     * sets the isAppMounted variable to true
     */
    const setIsAppMounted = () => {
        isAppMounted = true;
    };

    /**
     * Return isLoading value
     *
     * @returns {boolean} - true/false
     */
    const getIsLoading = () => {
        return isLoading;
    };

    /**
     * sets the isLoading variable to passed param true/false
     * this should be set in cases where you want to block UI momentarily, for example during animation.
     *
     * @param {boolean} value - true/false
     * @returns {undefined}
     */
    const setIsLoading = value => {
        isLoading = value;
    };

    /**
     * Gets view data
     *
     * @returns {Array} - array of story record
     */
    const getViewData = () => {
        return viewData;
    };

    return {
        config,
        viewData,
        filterData,
        zoomViewData,
        getContent,
        applyFilter,
        getActiveRoute,
        setActiveRoute,
        setActiveStory,
        getActiveStory,
        getTimelineViewData,
        getTimelineViewIndexes,
        triggerRelatedData,
        getFilterData,
        getSelectedFilterData,
        getZoomStoryData,
        setZoomStoryData,
        isReady,
        getStoryViewReferrer,
        setStoryViewReferrer,
        getIsAppMounted,
        setIsAppMounted,
        getIsLoading,
        setIsLoading,
        getViewData
    };
};

export default appData();
